import Input from '@/components/Input';
import Select from '@/components/Select';
import { useEffect, useState, useRef, useDeferredValue, useId } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@/components/Button';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdClear } from 'react-icons/md';
import { useController, useFormContext, useWatch, Controller } from 'react-hook-form';
import type { FormValue } from '@/routes/agent_spa/agent/shared_job_posts/index';
import Checkbox from '@/components/Checkbox';
import { useDebounce } from '@/hooks/useDebounce';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tag } from '@/components/Tag';
import SharedJobPostListFilterDialog from './SharedJobPostListFilterDialog';
import { MdCancel } from 'react-icons/md';
import { useQuery } from '@tanstack/react-query';
import type { AgentApiSharedJobPostsMetadata } from '@/types/serializers';
import { MaterialIcon } from '@/components/MaterialIcon';
import useSharedJobPostLists from '../hooks/useSharedJobPostLists';
import {
  PREFECTURES,
  EMPLOYMENT_TYPES,
  EDUCATION_LEVELS,
} from '@/constants/sharedJobPostConstants';

// 求人管理ページ用のソートオプション
//
// 「作成が新しい順 / 古い順」は OpenSearch mapping に created_at が無いため、
// 連番 id を作成順の代理として `id:desc` / `id:asc` で投げる (Issue #2629)。
// 表示ラベルは UX 維持のため「作成が新しい順 / 古い順」のまま。
const orderItems = [
  { value: 'updated_at:desc', label: '更新が新しい順' },
  { value: 'updated_at:asc', label: '更新が古い順' },
  { value: 'id:desc', label: '作成が新しい順' },
  { value: 'id:asc', label: '作成が古い順' },
  { value: 'fee_rate:desc', label: '成約料率が高い順' },
  { value: 'fee_value:desc', label: '成約報酬が高い順' },
];

// 文字列配列用のMultiSelectコンポーネント
interface MultiSelectStringFieldProps {
  values: string[];
  options: string[];
  placeholder: string;
  onUpdate: (values: string[]) => void;
  searchable?: boolean;
  align?: 'start' | 'end';
}

function MultiSelectStringField({
  values,
  options,
  placeholder,
  onUpdate,
  searchable = false,
  align = 'start',
}: MultiSelectStringFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const selectedValues = values.filter(Boolean);

  const filteredOptions = searchable
    ? options.filter((option) => option.toLowerCase().includes(deferredQuery.toLowerCase()))
    : options;

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const newValues = [...values];
    const index = newValues.indexOf(option);

    if (checked && index === -1) {
      newValues.push(option);
    } else if (!checked && index !== -1) {
      newValues.splice(index, 1);
    }

    onUpdate(newValues);
  };

  const removeValue = (valueToRemove: string) => {
    const newValues = values.filter((value) => value !== valueToRemove);
    onUpdate(newValues);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex h-xxl w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white p-2">
          <div className="flex flex-1 gap-2 overflow-x-auto pr-2 text-nowrap">
            {selectedValues.length > 0 ? (
              selectedValues.map((value) => (
                <div key={value} className="shrink-0">
                  <Tag color="default" className="flex items-center gap-1">
                    {value}
                    <button
                      type="button"
                      className="flex size-5 items-center justify-center rounded-full border-none bg-transparent p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(value);
                      }}
                    >
                      <MdCancel className="fill-main text-lg opacity-50" />
                    </button>
                  </Tag>
                </div>
              ))
            ) : (
              <span className="text-placeholder">{placeholder}</span>
            )}
          </div>
          {selectedValues.length > 0 && (
            <div className="pointer-events-none absolute inset-y-2 right-8 w-8 bg-gradient-to-l from-white to-transparent" />
          )}
          <MdKeyboardArrowDown
            className={`text-xl text-main transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align={align} className="z-[110] w-[724px] p-4">
        <div className="space-y-4">
          {searchable && (
            <Input
              search
              className="w-full"
              placeholder="フリーワードで検索"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          <div className="max-h-[264px] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              {filteredOptions.map((option) => (
                <Checkbox
                  key={option}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(checked) => handleCheckboxChange(option, checked)}
                >
                  <span className="font-normal">{option}</span>
                </Checkbox>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface Props {
  onSubmit: (data: FormValue) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  isSelectingAll?: boolean;
  isAllSelected?: boolean;
  totalCount?: number;
  hasResults?: boolean;
}

export default function SearchForm({
  onSubmit,
  onSelectAll,
  onDeselectAll,
  isSelectingAll,
  isAllSelected,
  totalCount = 0,
  hasResults,
}: Props) {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const isInitialMount = useRef(true);
  const searchDebounce = useDebounce(300);
  const [isListDialogOpen, setIsListPopoverOpen] = useState(false);

  const { register, watch, handleSubmit, control, setValue, reset } = useFormContext<FormValue>();
  const sortController = useController({ name: 'sort', control });
  const selectedListId = useWatch({ name: 'shared_job_post_list_id', control });

  const toHalfWidthDigits = (value: string) =>
    value.replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0));

  const registerNumber = (name: Parameters<typeof register>[0]) => {
    const field = register(name);
    return {
      ...field,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        e.target.value = toHalfWidthDigits(e.target.value);
        return field.onChange(e);
      },
    };
  };

  // プロバイダーメタデータの取得
  const { data: metadata } = useQuery<AgentApiSharedJobPostsMetadata>({
    queryKey: ['/agent/api/shared_job_posts/metadata'],
    queryFn: async () => {
      const response = await fetch('/agent/api/shared_job_posts/metadata.json');
      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }
      return response.json();
    },
  });

  // リスト一覧の取得
  const { data: listsData } = useSharedJobPostLists();

  const providerDisplayNameMap = metadata?.available_providers
    ? Object.fromEntries(metadata.available_providers.map((item) => [item.key, item.label]))
    : {};

  // プロバイダー選択肢の生成
  const providerItems = metadata?.permitted_providers
    ? [
        { value: 'all', label: 'すべて' },
        ...metadata.permitted_providers.map((provider) => ({
          value: provider,
          label: providerDisplayNameMap[provider] || provider,
        })),
      ]
    : [{ value: 'loading', label: '読み込み中...' }];

  // 勤務地（都道府県）は MultiSelectStringField 用にコード配列を県名配列へ変換する（Issue #3394 / #3397で都道府県に統一）
  const prefectureCodes = useWatch({ name: 'prefecture_codes', control }) ?? [];
  const prefectureNames = prefectureCodes
    .map((code) => PREFECTURES.find((p) => p.code === code)?.name)
    .filter((name): name is string => Boolean(name));

  const handlePrefectureUpdate = (names: string[]) => {
    const codes = names
      .map((name) => PREFECTURES.find((p) => p.name === name)?.code)
      .filter((code): code is number => code !== undefined);
    setValue('prefecture_codes', codes);
  };

  // 雇用形態は MultiSelectStringField 用に enum 値配列をラベル配列へ変換する（Issue #3216）
  const employmentTypes = useWatch({ name: 'employment_types', control }) ?? [];
  const employmentTypeLabels = employmentTypes
    .map((value) => EMPLOYMENT_TYPES.find((e) => e.value === value)?.label)
    .filter((label): label is string => Boolean(label));

  const handleEmploymentTypeUpdate = (labels: string[]) => {
    const values = labels
      .map((label) => EMPLOYMENT_TYPES.find((e) => e.label === label)?.value)
      .filter((value): value is string => value !== undefined);
    if (employmentTypes.length === 0 && values.length > 0) {
      setValue('employment_type_undisclosed', '');
    }
    setValue('employment_types', values);
  };

  const ageField = registerNumber('age');

  const educationLevelItems = [{ value: 'all', label: 'すべて' }, ...EDUCATION_LEVELS];

  const employmentTypeUndisclosedController = useController({
    name: 'employment_type_undisclosed',
    control,
  });
  const ageUndisclosedController = useController({ name: 'age_undisclosed', control });
  const educationLevelUndisclosedController = useController({
    name: 'education_level_undisclosed',
    control,
  });
  const jobTypeInexperiencedController = useController({
    name: 'job_type_inexperienced_ok',
    control,
  });
  const industryInexperiencedController = useController({
    name: 'industry_inexperienced_ok',
    control,
  });
  const inexperiencedLabelId = useId();

  // 自動検索機能 - フォームの変更を監視（デバウンス付き）
  useEffect(() => {
    // 初回レンダリング時はスキップ
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const subscription = watch(() => {
      searchDebounce(() => {
        handleSubmit(onSubmit)();
      });
    });

    return () => subscription.unsubscribe();
  }, [watch, handleSubmit, onSubmit, searchDebounce]);

  // 検索条件をクリアする関数
  const handleClearSearch = () => {
    const cleared: FormValue = {
      search_query: '',
      company_name: '',
      job_position_name: '',
      min_salary: '',
      max_salary: '',
      salary: '',
      salary_undisclosed: '',
      min_fee_rate: '',
      max_fee_rate: '',
      fee_rate: '',
      min_fee_value: '',
      max_fee_value: '',
      fee_value: '',
      sort: 'updated_at:desc',
      provider: undefined,
      shared_job_post_list_id: undefined,
      prefecture_codes: [],
      employment_types: [],
      // 未記載を含む3項目は既定ONに戻す（Issue #3216）
      employment_type_undisclosed: '1',
      age: '',
      age_undisclosed: '1',
      education_level: '',
      education_level_undisclosed: '1',
      job_type_inexperienced_ok: '',
      industry_inexperienced_ok: '',
    };

    // デフォルト値にリセット
    reset(cleared);

    // ローカルステートもリセット
    setIsDetailOpen(false);

    // 検索を実行
    onSubmit(cleared);
  };

  // 検索条件が設定されているかチェック
  const hasSearchConditions = () => {
    const currentValues = watch();

    // 各フィールドの値をチェック
    if (currentValues.search_query && currentValues.search_query.trim() !== '') return true;
    if (currentValues.company_name && currentValues.company_name.trim() !== '') return true;
    if (currentValues.salary && currentValues.salary.trim() !== '') return true;
    if (currentValues.min_salary && currentValues.min_salary.trim() !== '') return true;
    if (currentValues.max_salary && currentValues.max_salary.trim() !== '') return true;
    if (currentValues.min_fee_rate && currentValues.min_fee_rate.trim() !== '') return true;
    if (currentValues.max_fee_rate && currentValues.max_fee_rate.trim() !== '') return true;
    // fee_rate / fee_value は UI に入力欄がなくなったが、URL や localStorage から復元され
    // API に送信されて結果を絞り込み得るため、検索条件として扱い「検索条件をクリア」で解除できるようにする
    if (currentValues.fee_rate && currentValues.fee_rate.trim() !== '') return true;
    if (currentValues.min_fee_value && currentValues.min_fee_value.trim() !== '') return true;
    if (currentValues.max_fee_value && currentValues.max_fee_value.trim() !== '') return true;
    if (currentValues.fee_value && currentValues.fee_value.trim() !== '') return true;
    if (currentValues.salary_undisclosed === '1') return true;
    if (currentValues.job_position_name && currentValues.job_position_name.trim() !== '')
      return true;
    if (currentValues.prefecture_codes && currentValues.prefecture_codes.length > 0) return true;
    // 未記載を含む3項目（*_undisclosed）は既定 ON のため検索条件として数えない（Issue #3216）
    if (currentValues.employment_types && currentValues.employment_types.length > 0) return true;
    if (currentValues.age && currentValues.age.trim() !== '') return true;
    if (currentValues.education_level && currentValues.education_level.trim() !== '') return true;
    if (currentValues.job_type_inexperienced_ok === '1') return true;
    if (currentValues.industry_inexperienced_ok === '1') return true;
    if (currentValues.provider && currentValues.provider.trim() !== '') return true;
    if (
      currentValues.shared_job_post_list_id &&
      currentValues.shared_job_post_list_id.trim() !== ''
    )
      return true;

    return false;
  };

  return (
    <>
      {/* 検索フォーム本体 */}
      <div className="w-full rounded bg-white p-4 shadow-layer1">
        <div className="space-y-4">
          {/* 1段目: フリーワード（横断検索）+ 年収 */}
          {/* 横断検索 (Issue #2465): タイトル / 業務サマリ / 必須スキル / 企業名 / ポジションを横断検索 */}
          <div className="flex flex-wrap gap-lg">
            <div className="flex w-[450px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">フリーワード</label>
              <Input
                search
                placeholder="フリーワードで絞り込む"
                {...register('search_query')}
                className="w-full"
              />
            </div>

            {/* 年収検索（含む + レンジ）*/}
            <div className="flex min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">年収</label>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                  <Input {...registerNumber('salary')} placeholder="0" className="w-[100px]" />
                  <span className="text-sm">万円を含む</span>
                </div>
                <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                  <Input {...registerNumber('min_salary')} placeholder="0" className="w-[100px]" />
                  <span className="text-sm">万円</span>
                  <span className="text-sm">〜</span>
                  <Input {...registerNumber('max_salary')} placeholder="0" className="w-[100px]" />
                  <span className="text-sm">万円</span>
                </div>
              </div>
            </div>
          </div>

          {/* 2段目: 企業名 + ポジション + 勤務地 */}
          <div className="flex flex-wrap gap-lg">
            {/* 企業名フィルタ */}
            <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">企業名</label>
              <Input
                placeholder="企業名を入力して検索"
                {...register('company_name')}
                className="w-full"
              />
            </div>

            {/* ポジション検索（フリーワード、空白区切りで OR 部分一致） */}
            <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">ポジション</label>
              <Input
                search
                placeholder="ポジションで絞り込む"
                {...register('job_position_name')}
                className="w-full"
              />
            </div>

            {/* 勤務地（都道府県コード）絞り込み（Issue #3394 / #3397でフリーワード勤務地と統合） */}
            <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
              <label className="text-sm font-bold">勤務地</label>
              <MultiSelectStringField
                values={prefectureNames}
                options={PREFECTURES.map((p) => p.name)}
                placeholder="勤務地を選択"
                onUpdate={handlePrefectureUpdate}
                searchable
                align="start"
              />
            </div>
          </div>

          {/* 詳細検索アコーディオンボタン */}
          <div className="flex items-center">
            <Button
              color="tertiary"
              size="md"
              type="button"
              onClick={() => setIsDetailOpen(!isDetailOpen)}
            >
              {isDetailOpen ? (
                <MdKeyboardArrowUp className="size-icon-xs text-primary" />
              ) : (
                <MdKeyboardArrowDown className="size-icon-xs text-primary" />
              )}
              <span className="text-sm font-bold text-primary">
                {isDetailOpen ? '詳細条件を閉じる' : '詳細条件を開く'}
              </span>
            </Button>
          </div>

          {/* 詳細検索アコーディオンコンテンツ: 取り込み元 + 成約料率 + 成約金額 + リストで絞り込む */}
          {isDetailOpen && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-lg">
                {/* 取り込み元（企業名・ポジション・勤務地と同一幅 300px） */}
                {metadata?.permitted_providers && metadata.permitted_providers.length > 0 && (
                  <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
                    <label className="text-sm font-bold">取り込み元</label>
                    <Controller
                      name="provider"
                      control={control}
                      render={({ field }) => (
                        <Select
                          className="w-full"
                          items={providerItems}
                          value={field.value || 'all'}
                          onValueChange={(value) =>
                            setValue('provider', value === 'all' ? undefined : value)
                          }
                        />
                      )}
                    />
                  </div>
                )}

                {/* 成約料率検索（レンジ）*/}
                <div className="flex min-w-0 flex-col gap-2">
                  <label className="text-sm font-bold">成約料率</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                      <Input
                        {...registerNumber('min_fee_rate')}
                        placeholder="0"
                        className="w-[100px]"
                      />
                      <span className="text-sm">%</span>
                      <span className="text-sm">〜</span>
                      <Input
                        {...registerNumber('max_fee_rate')}
                        placeholder="0"
                        className="w-[100px]"
                      />
                      <span className="text-sm">%</span>
                    </div>
                  </div>
                </div>

                {/* 成約金額検索（レンジ）*/}
                <div className="flex min-w-0 flex-col gap-2">
                  <label className="text-sm font-bold">成約金額</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                      <Input
                        {...registerNumber('min_fee_value')}
                        placeholder="0"
                        className="w-[100px]"
                      />
                      <span className="text-sm">万円</span>
                      <span className="text-sm">〜</span>
                      <Input
                        {...registerNumber('max_fee_value')}
                        placeholder="0"
                        className="w-[100px]"
                      />
                      <span className="text-sm">万円</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 雇用形態 + 応募可能年齢 + 最終学歴（Issue #3216） */}
              <div className="flex flex-wrap items-start gap-lg">
                {/* 雇用形態（複数選択） */}
                <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
                  <label className="text-sm font-bold">雇用形態</label>
                  <MultiSelectStringField
                    values={employmentTypeLabels}
                    options={EMPLOYMENT_TYPES.map((e) => e.label)}
                    placeholder="雇用形態を選択"
                    onUpdate={handleEmploymentTypeUpdate}
                  />
                  <Checkbox
                    checked={employmentTypeUndisclosedController.field.value === '1'}
                    onChange={(checked) =>
                      employmentTypeUndisclosedController.field.onChange(checked ? '1' : '')
                    }
                  >
                    <span className="text-sm font-normal">未記載を含む</span>
                  </Checkbox>
                </div>

                {/* 応募可能年齢: ◯歳の候補者が応募できる求人で絞り込む */}
                <div className="flex min-w-0 flex-col gap-2">
                  <label className="text-sm font-bold">応募可能年齢</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      {...ageField}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        if (!watch('age') && e.target.value.trim() !== '') {
                          setValue('age_undisclosed', '');
                        }
                        return ageField.onChange(e);
                      }}
                      placeholder="0"
                      className="w-[100px]"
                    />
                    <span className="text-sm">歳が応募可能</span>
                  </div>
                  <Checkbox
                    checked={ageUndisclosedController.field.value === '1'}
                    onChange={(checked) =>
                      ageUndisclosedController.field.onChange(checked ? '1' : '')
                    }
                  >
                    <span className="text-sm font-normal">未記載を含む</span>
                  </Checkbox>
                </div>

                {/* 最終学歴: ◯◯卒で応募できる求人で絞り込む */}
                <div className="flex w-[300px] max-w-full min-w-0 flex-col gap-2">
                  <label className="text-sm font-bold">最終学歴</label>
                  <Controller
                    name="education_level"
                    control={control}
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        items={educationLevelItems}
                        value={field.value || 'all'}
                        onValueChange={(value) => {
                          if (!field.value && value !== 'all') {
                            setValue('education_level_undisclosed', '');
                          }
                          setValue('education_level', value === 'all' ? undefined : value);
                        }}
                      />
                    )}
                  />
                  <Checkbox
                    checked={educationLevelUndisclosedController.field.value === '1'}
                    onChange={(checked) =>
                      educationLevelUndisclosedController.field.onChange(checked ? '1' : '')
                    }
                  >
                    <span className="text-sm font-normal">未記載を含む</span>
                  </Checkbox>
                </div>

                {/* 未経験可: 求人票に記載のある求人のみ（Issue #3529） */}
                <div
                  role="group"
                  aria-labelledby={inexperiencedLabelId}
                  className="flex min-w-0 flex-col gap-2"
                >
                  <span id={inexperiencedLabelId} className="text-sm font-bold">
                    未経験可
                  </span>
                  <div className="flex flex-wrap items-center gap-md">
                    <Checkbox
                      checked={jobTypeInexperiencedController.field.value === '1'}
                      onChange={(checked) =>
                        jobTypeInexperiencedController.field.onChange(checked ? '1' : '')
                      }
                    >
                      <span className="text-sm font-normal">職種未経験可</span>
                    </Checkbox>
                    <Checkbox
                      checked={industryInexperiencedController.field.value === '1'}
                      onChange={(checked) =>
                        industryInexperiencedController.field.onChange(checked ? '1' : '')
                      }
                    >
                      <span className="text-sm font-normal">業種未経験可</span>
                    </Checkbox>
                  </div>
                </div>
              </div>

              {/* リストで絞り込む */}
              <div>
                <Button
                  color="secondary"
                  size="md"
                  type="button"
                  onClick={() => setIsListPopoverOpen(true)}
                >
                  <MaterialIcon name="search" className="text-xl" />
                  <span className="text-sm font-bold">
                    {listsData?.records?.find((l) => String(l.id) === selectedListId)?.name ??
                      'リストで絞り込む'}
                  </span>
                </Button>
                <SharedJobPostListFilterDialog
                  open={isListDialogOpen}
                  onOpenChange={setIsListPopoverOpen}
                  lists={listsData?.records ?? []}
                  providers={
                    metadata?.available_providers?.filter((ap) =>
                      listsData?.providers?.some(
                        (p) => p.provider === ap.key && p.connection_status === 'active'
                      )
                    ) ?? []
                  }
                  providerSyncInfos={listsData?.providers ?? []}
                  onSelectList={(listId) => setValue('shared_job_post_list_id', listId)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ソート + 検索条件クリア */}
      <div className="mt-lg flex items-end justify-between gap-4">
        <div className="flex w-[196px] shrink-0 flex-col gap-2">
          <Select
            className="w-full"
            value={sortController.field.value}
            onValueChange={sortController.field.onChange}
            items={orderItems}
          />
        </div>
        <div className="flex items-center gap-4">
          {hasSearchConditions() &&
            hasResults &&
            onSelectAll &&
            (isAllSelected ? (
              <Button color="tertiary" size="md" type="button" onClick={onDeselectAll}>
                <span className="text-sm font-bold text-primary">全選択を解除</span>
              </Button>
            ) : (
              <Button
                color="tertiary"
                size="md"
                type="button"
                onClick={onSelectAll}
                disabled={isSelectingAll}
              >
                <span className="text-sm font-bold text-primary">
                  {isSelectingAll ? '選択中...' : `全${totalCount}件を選択`}
                </span>
              </Button>
            ))}
          {hasSearchConditions() && (
            <Button color="tertiary" size="md" type="button" onClick={handleClearSearch}>
              <MdClear className="size-icon-xs text-primary" />
              <span className="text-sm font-bold text-primary">検索条件をクリア</span>
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
